import { useEffect, useRef, useState } from 'react';

import { FORM_ID } from '../constants';
import type { FormState, PreviewData } from '../types';

const PREVIEW_SYNC_DELAY_MS = 100;

export function useNewDappPreviewData(formData: FormState) {
  const [previewData, setPreviewData] = useState<PreviewData>({
    dappName: formData.dappName,
    subtitle: formData.subtitle,
    description: formData.description,
    dappIcon: formData.dappIcon,
    banner: formData.banner,
    dappPreviews: formData.dappPreviews,
    tokenDistribution: formData.tokenDistribution,
  });
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const updatePreviewData = () => {
      const form = document.getElementById(FORM_ID);
      if (!form) {
        return;
      }

      const nameInput = form.querySelector(
        '[name="dappName"]',
      ) as HTMLInputElement | null;
      const subtitleInput = form.querySelector(
        '[name="subtitle"]',
      ) as HTMLInputElement | null;
      const descriptionInput = form.querySelector(
        '[name="description"]',
      ) as HTMLTextAreaElement | null;

      if (!nameInput && !subtitleInput && !descriptionInput) {
        return;
      }

      setPreviewData((prev) => ({
        ...prev,
        ...(nameInput ? { dappName: nameInput.value } : {}),
        ...(subtitleInput ? { subtitle: subtitleInput.value } : {}),
        ...(descriptionInput ? { description: descriptionInput.value } : {}),
      }));
    };

    const handleInputChange = () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }

      previewDebounceRef.current = setTimeout(
        updatePreviewData,
        PREVIEW_SYNC_DELAY_MS,
      );
    };

    const form = document.getElementById(FORM_ID);
    if (!form) {
      return;
    }

    const nameInput = form.querySelector('[name="dappName"]');
    const subtitleInput = form.querySelector('[name="subtitle"]');
    const descriptionInput = form.querySelector('[name="description"]');

    nameInput?.addEventListener('input', handleInputChange);
    subtitleInput?.addEventListener('input', handleInputChange);
    descriptionInput?.addEventListener('input', handleInputChange);

    updatePreviewData();

    return () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }

      nameInput?.removeEventListener('input', handleInputChange);
      subtitleInput?.removeEventListener('input', handleInputChange);
      descriptionInput?.removeEventListener('input', handleInputChange);
    };
  }, []);

  useEffect(() => {
    setPreviewData((prev) => {
      if (
        formData.dappIcon === prev.dappIcon &&
        formData.banner === prev.banner &&
        formData.dappPreviews === prev.dappPreviews &&
        formData.tokenDistribution === prev.tokenDistribution
      ) {
        return prev;
      }

      return {
        ...prev,
        dappIcon: formData.dappIcon,
        banner: formData.banner,
        dappPreviews: formData.dappPreviews,
        tokenDistribution: formData.tokenDistribution,
      };
    });
  }, [
    formData.dappIcon,
    formData.banner,
    formData.dappPreviews,
    formData.tokenDistribution,
  ]);

  return previewData;
}
